"use client"

import { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import Swal from "sweetalert2"
import Header from "../components/Header"
import Footer from "../components/Footer"

import { API_BASE_URL } from "../config/apiConfig"


const SaleteamLogin = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/companies/public/list`)
        const data = await res.json()
        if (data.success && Array.isArray(data.data)) {
          setCompanies(data.data)
        }
      } catch (err) {
        console.error("Failed to load companies:", err)
      }
    }
    fetchCompanies()
  }, [])

  const decodeJWT = (token) => {
    try {
      const base64Url = token.split(".")[1]
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
      return JSON.parse(jsonPayload)
    } catch (e) {
      return null
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    if (!selectedCompany) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select a company.",
      })
      setLoading(false)
      return
    }

    try {
      // 1. Authenticate using the new dedicated salesman login API
      const response = await fetch(`${API_BASE_URL}/users/salesman-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, companyId: selectedCompany }),
      })

      const data = await response.json()

      if (data.success && data.data?.token) {
        const { token, companySlug } = data.data

        if (companySlug) {
          localStorage.setItem("companyAuthToken", token)
          localStorage.removeItem("authToken")
          
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Access Granted. Redirecting to portal...",
            timer: 1500,
            showConfirmButton: false,
          })

          setTimeout(() => {
            window.location.href = `/${companySlug}`
          }, 1500)
        } else {
           Swal.fire({
            icon: "error",
            title: "Error",
            text: "Your account is not linked to an active company portal.",
          })
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: data.message || "Invalid credentials.",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Connection failed. Please check your internet.",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Header />
      <section className="hero" style={{ minHeight: "100vh", paddingTop: "100px" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-5 mt-5">
              <div className="card border-0 shadow position-relative" style={{ borderRadius: "30px", overflow: "visible" }}>
                {/* Circular Icon Overlap */}
                <div 
                  className="position-absolute top-0 start-50 translate-middle shadow" 
                  style={{ 
                    width: "100px", 
                    height: "100px", 
                    backgroundColor: "#00B5AD", 
                    borderRadius: "50%", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    border: "5px solid #fff",
                    zIndex: "10"
                  }}
                >
                  <i className="bi bi-person-fill text-white" style={{ fontSize: "3rem" }}></i>
                </div>

                <div className="card-body p-5 pt-5 mt-4">
                  <div className="text-center mb-4 mt-2">
                    <h4 className="fw-bold" style={{ color: "#002B5C" }}>Saleteam Login</h4>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <div className="input-group">
                        <span className="input-group-text border-0" style={{ backgroundColor: "#f0f2f5", borderRadius: "50px 0 0 50px", paddingLeft: "20px" }}>
                          <i className="bi bi-building text-muted"></i>
                        </span>
                        <select
                          className="form-select border-0"
                          value={selectedCompany}
                          onChange={(e) => setSelectedCompany(e.target.value)}
                          required
                          style={{ backgroundColor: "#f0f2f5", borderRadius: "0 50px 50px 0", height: "50px", boxShadow: "none" }}
                        >
                          <option value="">Choose Company</option>
                          {companies.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.companyName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="input-group">
                        <span className="input-group-text border-0" style={{ backgroundColor: "#f0f2f5", borderRadius: "50px 0 0 50px", paddingLeft: "20px" }}>
                          <i className="bi bi-person-fill text-muted"></i>
                        </span>
                        <input
                          type="email"
                          className="form-control border-0"
                          placeholder="Username"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          style={{ backgroundColor: "#f0f2f5", borderRadius: "0 50px 50px 0", height: "50px", boxShadow: "none" }}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="input-group position-relative">
                        <span className="input-group-text border-0" style={{ backgroundColor: "#f0f2f5", borderRadius: "50px 0 0 50px", paddingLeft: "20px" }}>
                          <i className="bi bi-lock-fill text-muted"></i>
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control border-0"
                          placeholder="••••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          style={{ backgroundColor: "#f0f2f5", borderRadius: "0 50px 50px 0", height: "50px", boxShadow: "none", paddingRight: "50px" }}
                        />
                        <button
                          type="button"
                          className="btn position-absolute top-50 end-0 translate-middle-y border-0 pe-4"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ zIndex: 10, background: "transparent" }}
                          tabIndex="-1"
                        >
                          <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"} text-muted`}></i>
                        </button>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4 px-2" style={{ fontSize: "0.85rem" }}>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="rememberMe" style={{ cursor: "pointer" }} />
                        <label className="form-check-label text-muted" htmlFor="rememberMe" style={{ cursor: "pointer" }}>
                          Remember me
                        </label>
                      </div>
                      <Link to="/saleteam-forgot-password" className="text-decoration-none text-muted" style={{ fontStyle: "italic" }}>
                        Forgot Password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-turquoise w-100 rounded-pill fw-bold text-uppercase"
                      disabled={loading}
                      style={{ height: "50px", fontSize: "1rem", letterSpacing: "1px" }}
                    >
                      {loading ? <span className="spinner-border spinner-border-sm"></span> : "Login"}
                    </button>
                    
                    <div className="text-center mt-4">
                      <Link to="/" className="text-muted small text-decoration-none">
                        <i className="bi bi-arrow-left me-1"></i> Back to Home
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default SaleteamLogin
